import { type EmbedCommand, parseEmbedNav } from '@crow/ui/embed';
import { type RemotePageTarget, remotePagePartition } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import { ExternalLink, RotateCw } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AppConnectScreen } from '@/features/apps/app-connect-screen';
import { appRemotePage } from '@/features/apps/apps-client';
import { connectRemoteApp, setRemoteNav } from '@/features/apps/remote-app-bridge';
import { isElectronDesktop } from '@/lib/electron-bridge';
import { useRouter } from '@/lib/navigation';
import { CONNECTIONS_CHANGED_EVENT } from '@/lib/plugins/connection-events';
import { onPluginsChanged } from '@/lib/plugins/plugin-native-client';
import { pluginPagePath, pluginsPath } from '@/lib/plugins/plugin-sections';

type RemoteState = { status: 'loading' } | { status: 'error'; message: string } | RemotePageTarget;

type RemoteWebview = HTMLElement & {
  reload(): void;
  send(channel: string, message: unknown): void;
};

/** The IPC channel of the embed bridge between a remote page's guest preload and the host. */
export const EMBED_CHANNEL = 'crow:embed';

/**
 * An app's remote web app (`page.url`), in its tab: on the origin its manifest or the user's
 * connection gives, which the server resolves. Desktop shows it in a `<webview>` of the app's own
 * partition (see the Desktop `RemotePageWebviews`, which keeps it on that origin); the web frames
 * a Crowlingo application (`embeddable`) and opens any other page in a new tab.
 *
 * A Crowlingo application talks to the host through the embed bridge (`@crow/ui/embed`): what it
 * says it shows lands under `bridgeKey` (`remote-app-bridge.ts`), which the host's frame and left
 * column read, and the host's commands reach it the same way. `path` opens one of its items (a
 * deep link): the page loads there, and a later path is sent to it rather than reloading it.
 */
export function RemoteAppPanel({
  plugin,
  title,
  description,
  iconSrc,
  visible,
  bridgeKey,
  path = null,
}: {
  plugin: string;
  /** Where its navigation is kept for the host's frame and column. */
  bridgeKey?: string | undefined;
  /** An item to open (`/files/<id>`), on the page's origin. */
  path?: string | null | undefined;
  title: string;
  /** What the app does, for its Connect screen. */
  description?: string | undefined;
  iconSrc?: string | undefined;
  /** Resolved again each time the tab shows. */
  visible: boolean;
}) {
  const [state, setState] = useState<RemoteState>({ status: 'loading' });
  // Desktop asks for a one-time sign-in URL until its webview has opened: later resolves (each
  // focus) keep the page where it is, signed in by then.
  const opened = useRef(false);
  // The page loads at the first path; later ones are sent to it (`crow:navigate`).
  const firstPath = useRef(path);
  // The origin is the user's account: resolved again when the accounts or the apps change, when
  // an authorization finishing in another window brings the app back, and when the tab shows.
  const [accounts, setAccounts] = useState(0);
  useEffect(() => {
    const changed = () => setAccounts((value) => value + 1);
    window.addEventListener(CONNECTIONS_CHANGED_EVENT, changed);
    window.addEventListener('focus', changed);
    const stopApps = onPluginsChanged(changed);
    return () => {
      stopApps();
      window.removeEventListener(CONNECTIONS_CHANGED_EVENT, changed);
      window.removeEventListener('focus', changed);
    };
  }, []);
  useEffect(() => {
    void accounts;
    if (!visible) return;
    let cancelled = false;
    appRemotePage(plugin, isElectronDesktop() && !opened.current, firstPath.current).then(
      (target) => {
        if (cancelled) return;
        if (target.status === 'ready') opened.current = true;
        setState(target);
      },
      (reason: unknown) => {
        if (!cancelled)
          setState({
            status: 'error',
            message: reason instanceof Error ? reason.message : String(reason),
          });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [plugin, accounts, visible]);

  if (state.status === 'loading') return <div className="size-full" />;
  const bridge =
    bridgeKey === undefined || state.status !== 'ready' || state.embeddable !== true
      ? undefined
      : { key: bridgeKey, path };
  if (state.status === 'error')
    return <RemoteNotice detail={state.message} plugin={plugin} title={title} />;
  // Its account is not connected yet: one screen that connects it (D29d).
  if (state.status === 'unconfigured')
    return (
      <AppConnectScreen description={description} iconSrc={iconSrc} plugin={plugin} title={title} />
    );
  return isElectronDesktop() ? (
    <RemoteWebviewPage
      bridge={bridge}
      origin={state.origin}
      partition={remotePagePartition(plugin)}
      signInUrl={state.signInUrl}
      url={state.url}
    />
  ) : state.embeddable === true ? (
    <RemoteFramePage bridge={bridge} origin={state.origin} title={title} url={state.url} />
  ) : (
    <RemoteNotice
      action={
        <Button asChild size="sm">
          <a href={state.url} rel="noopener noreferrer" target="_blank">
            <ExternalLink className="size-3.5" />
            Open {title}
          </a>
        </Button>
      }
      detail={`${title} opens in a new tab on ${state.origin}.`}
      title={title}
    />
  );
}

type BridgeProps = { key: string; path: string | null } | undefined;

/**
 * Keeps the page shown under the bridge's key connected (its origin, how to send it a command),
 * and sends it a path asked for after it loaded.
 */
function useBridge(
  bridge: BridgeProps,
  origin: string,
  send: ((command: EmbedCommand) => void) | null,
): void {
  const key = bridge?.key;
  useEffect(() => {
    if (key === undefined || send === null) return;
    connectRemoteApp(key, { origin, send });
    return () => connectRemoteApp(key, null);
  }, [key, origin, send]);
  const path = bridge?.path ?? null;
  const loadedPath = useRef(path);
  useEffect(() => {
    if (path === null || send === null || path === loadedPath.current) return;
    loadedPath.current = path;
    send({ type: 'crow:navigate', url: path });
  }, [path, send]);
}

/** A Crowlingo application framed on the web, told it is embedded, talking over `postMessage`. */
function RemoteFramePage({
  url,
  origin,
  title,
  bridge,
}: {
  url: string;
  origin: string;
  title: string;
  bridge: BridgeProps;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [send, setSend] = useState<((command: EmbedCommand) => void) | null>(null);
  const key = bridge?.key;
  useEffect(() => {
    const frame = frameRef.current;
    if (frame === null || key === undefined) return;
    // Only the frame's own window, on the origin it was opened on.
    const heard = (event: MessageEvent) => {
      if (event.source !== frame.contentWindow || event.origin !== origin) return;
      const nav = parseEmbedNav(event.data);
      if (nav !== null) setRemoteNav(key, nav);
    };
    window.addEventListener('message', heard);
    setSend(() => (command: EmbedCommand) => frame.contentWindow?.postMessage(command, origin));
    return () => {
      window.removeEventListener('message', heard);
      setSend(null);
      setRemoteNav(key, null);
    };
  }, [key, origin]);
  useBridge(bridge, origin, send);
  return (
    <iframe
      className="size-full border-0"
      data-remote-frame
      ref={frameRef}
      src={url}
      title={title}
      // The application's own page, signed in with its cookies (the apps share a site); a
      // CrowRoom meeting needs the camera, the microphone and the screen.
      allow="clipboard-write; fullscreen; camera; microphone; display-capture; autoplay"
    />
  );
}

function RemoteWebviewPage({
  url,
  origin,
  partition,
  signInUrl,
  bridge,
}: {
  bridge?: BridgeProps;
  url: string;
  origin: string;
  partition: string;
  /** Loaded instead of `url` when the webview is created; it lands on `url` signed in. */
  signInUrl?: string | undefined;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  // The URL it mounted with, used by the first webview only: a ticket works once.
  const signInRef = useRef(signInUrl);
  const webviewRef = useRef<RemoteWebview | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [send, setSend] = useState<((command: EmbedCommand) => void) | null>(null);
  const bridgeKey = bridge?.key;
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // The partition must be set before the element attaches: React would set it too late.
    const webview = document.createElement('webview') as RemoteWebview;
    webview.setAttribute('partition', partition);
    // Same origin as `url`, so the Desktop guard lets it load; it is used once.
    webview.setAttribute('src', signInRef.current ?? url);
    signInRef.current = undefined;
    // Without it `target="_blank"` links do nothing. Desktop never opens the window: the page
    // loads it in place on its origin, or sends it to the user's browser.
    webview.setAttribute('allowpopups', '');
    webview.style.display = 'flex';
    webview.style.height = '100%';
    webview.style.width = '100%';
    const navigated = () => setFailure(null);
    const failed = (event: Event) => {
      const { errorCode, errorDescription, isMainFrame } = event as Event & {
        errorCode: number;
        errorDescription: string;
        isMainFrame: boolean;
      };
      // -3 is an aborted load (a navigation sent to the browser instead), not a failure.
      if (isMainFrame && errorCode !== -3) setFailure(errorDescription || `Error ${errorCode}`);
    };
    // The embed bridge: the guest preload's messages (`window.crowEmbedHost`, Desktop's
    // `remote-page-preload`), over the webview's IPC.
    const heard = (event: Event) => {
      const { channel, args } = event as Event & { channel: string; args: unknown[] };
      if (channel !== EMBED_CHANNEL || bridgeKey === undefined) return;
      const nav = parseEmbedNav(args[0]);
      if (nav !== null) setRemoteNav(bridgeKey, nav);
    };
    webview.addEventListener('did-navigate', navigated);
    webview.addEventListener('did-fail-load', failed);
    webview.addEventListener('ipc-message', heard);
    host.replaceChildren(webview);
    webviewRef.current = webview;
    if (bridgeKey !== undefined)
      setSend(() => (command: EmbedCommand) => {
        try {
          webview.send(EMBED_CHANNEL, command);
        } catch {
          // Not attached yet, or gone: the command is lost, as a click on a page still loading.
        }
      });
    return () => {
      webviewRef.current = null;
      setSend(null);
      if (bridgeKey !== undefined) setRemoteNav(bridgeKey, null);
      webview.remove();
    };
  }, [url, partition, bridgeKey]);
  useBridge(bridge, origin, send);
  // The app's own navigation is enough: the page fills the content area, with no browser bar.
  return (
    <div className="relative size-full">
      <div className="size-full" ref={hostRef} />
      {failure !== null && (
        <div className="absolute inset-0 bg-background">
          <RemoteNotice
            action={
              <Button onClick={() => webviewRef.current?.reload()} size="sm" variant="outline">
                <RotateCw className="size-3.5" />
                Retry
              </Button>
            }
            detail={failure}
            title={`${new URL(origin).host} could not be reached`}
          />
        </div>
      )}
    </div>
  );
}

function RemoteNotice({
  title,
  detail,
  action,
  plugin,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
  /** The plugin whose page the default action opens. */
  plugin?: string;
}) {
  const router = useRouter();
  return (
    <div className="flex size-full flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="type-ui font-medium text-foreground">{title}</p>
      <p className="type-ui max-w-md text-muted-foreground">{detail}</p>
      {action ?? (
        <Button
          onClick={() =>
            router.push(plugin ? pluginPagePath('installed', plugin) : pluginsPath('installed'))
          }
          size="sm"
          variant="outline"
        >
          Open plugin
        </Button>
      )}
    </div>
  );
}
